import { useState, useTransition } from 'react';
import { StarButton } from './StarButton';
import useTranslations from '@/hooks/useTranslations';
// import companyFavouriteCarpiProductConfiguration from '@/actions/productWorkflow/carpi/productConfiguration/companyFavouriteCarpiProductConfiguration';
import useNotification from '@/hooks/useNotification';
import useCompanyUnfavourite from '@/hooks/carpi/useCompanyUnfavourite';
import useCompanyUnfavouriteDialog from '@/hooks/carpi/useCompanyUnfavouriteDialog';
import { ProductConfiguration } from '@/types/productConfiguration';
import useCompanyFavourite from '@/hooks/carpi/useCompanyFavourite';
import { WorkflowCategories } from '@/types/fragments';
import type { DecisionDialogAnswer } from '@/types/decisionFlow';

interface CompanyFavouriteProps {
  // For first page: pass the existing config ID
  existingConfigId?: string;
  // The existing company-favourited config (if it exists)
  hasAlreadyBeenCompanyFavourited?: ProductConfiguration;
  isOutcomePage?: boolean;
  product?: WorkflowCategories;
  answers?: Record<string, DecisionDialogAnswer>;
}

export default function CompanyFavourite({
  existingConfigId,
  hasAlreadyBeenCompanyFavourited,
  isOutcomePage = false,
  product,
  answers,
}: CompanyFavouriteProps) {
  const [isLoading, startTransition] = useTransition();
  const [isCompanyFavourited, setIsCompanyFavourited] = useState(!!hasAlreadyBeenCompanyFavourited);

  const notifications = useNotification();
  const { t } = useTranslations();

  // Get the actual config ID to work with
  const configId = existingConfigId || hasAlreadyBeenCompanyFavourited?.id;

  const { handleCompanyFavouriting } = useCompanyFavourite(existingConfigId, answers);

  const { handleCompanyUnfavouriting } = useCompanyUnfavourite({
    existingCarpiProductConfigurationId: configId,
    setIsCompanyFavourited,
  });

  const { handleCompanyUnfavouritingDialog } = useCompanyUnfavouriteDialog({
    id: configId,
    product,
  });

  const handleToggle = async () => {
    startTransition(async () => {
      try {
        if (isOutcomePage) {
          // OUTCOME PAGE LOGIC
          if (isCompanyFavourited) {
            // Remove company reference
            await handleCompanyUnfavouriting();
            setIsCompanyFavourited(false);
          } else {
            // Create new config with company reference
            try {
              await handleCompanyFavouriting();
              setIsCompanyFavourited(true);
            } catch (error) {
              console.error(error);
            }
          }
        } else {
          // FIRST PAGE LOGIC
          if (isCompanyFavourited) {
            // Show dialog to confirm removal of company reference
            handleCompanyUnfavouritingDialog();
          }
        }
      } catch (error) {
        console.error('Error toggling company favourite:', error);
        notifications.error(t('product.workflow.companyFavourite.error'));
      }
    });
  };

  return (
    <StarButton
      onClick={handleToggle}
      leftPosition={isOutcomePage}
      border={true}
      backgroundColor={true}
      isFavourited={isCompanyFavourited}
      ariaLabel={t('cards.companyFavourite')}
      smallStar={true}
      text={t('product.workflow.companyFavourite.title')}
      disabled={isLoading}
    />
  );
}
